import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Separator,
} from "@repo/ui";
import { ArrowRight, LineChart, ShieldCheck, Sparkles, Workflow, Zap } from "lucide-react";

export function Home() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="px-6 md:px-10 py-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="size-8 rounded-md bg-primary grid place-items-center">
            <Zap className="size-5 text-primary-foreground" />
          </div>
          <div>
            <p className="text-sm uppercase tracking-widest text-muted-foreground">
              James Theeranon
            </p>
            <p className="font-semibold">Digital Transformation</p>
          </div>
        </div>
        <div className="hidden md:flex items-center gap-3">
          <Button variant="ghost" className="text-sm">
            Services
          </Button>
          <Button variant="ghost" className="text-sm">
            Approach
          </Button>
          <Button variant="ghost" className="text-sm">
            Work
          </Button>
          <Button variant="default" className="text-sm">
            Start a project
          </Button>
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className="px-6 md:px-10 pt-10 md:pt-16 pb-8 md:pb-12">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div className="space-y-6">
              <Badge variant="secondary" className="rounded-full border-border/60">
                for leaders ready to move
              </Badge>
              <h1 className="text-4xl md:text-6xl font-extrabold leading-tight">
                Transform ideas into measurable business impact.
              </h1>
              <p className="text-base md:text-lg text-muted-foreground max-w-prose">
                I help executives and teams architect, build, and scale digital capabilities — from
                strategy to product, AI adoption, and data-driven operations.
              </p>
              <div className="flex flex-wrap gap-3">
                <Button className="gap-2">
                  Work with James
                  <ArrowRight className="size-4" />
                </Button>
                <Button variant="outline" className="gap-2">
                  View capabilities
                </Button>
              </div>
              <div className="flex items-center gap-6 pt-2 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="size-4 text-primary" />
                  Trusted by SMEs & enterprise
                </div>
                <div className="flex items-center gap-2">
                  <LineChart className="size-4 text-primary" />
                  Outcomes over output
                </div>
              </div>
            </div>
            <div className="rounded-xl overflow-hidden border border-border">
              <img
                src="https://images.pexels.com/photos/6159817/pexels-photo-6159817.jpeg"
                alt="Minimal workspace with laptop and clock"
                className="w-full h-[320px] md:h-[460px] object-cover"
              />
            </div>
          </div>
        </section>

        <Separator className="opacity-20" />

        {/* Services */}
        <section className="px-6 md:px-10 py-12 md:py-16">
          <div className="mb-8">
            <h2 className="text-2xl md:text-3xl font-bold">Capabilities</h2>
            <p className="text-muted-foreground mt-2 max-w-prose">
              Practical transformation across strategy, product, and technology.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="bg-secondary/50 border-border/60">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="size-5 text-primary" />
                  Strategy & Advisory
                </CardTitle>
                <CardDescription>North star, roadmaps, org design.</CardDescription>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground space-y-2">
                <ul className="list-disc pl-5 space-y-1">
                  <li>Digital strategy & OKRs</li>
                  <li>Operating model & governance</li>
                  <li>Vendor & platform selection</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-secondary/50 border-border/60">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Workflow className="size-5 text-primary" />
                  Product & Delivery
                </CardTitle>
                <CardDescription>Ship value faster.</CardDescription>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground space-y-2">
                <ul className="list-disc pl-5 space-y-1">
                  <li>Product discovery & UX</li>
                  <li>Modern web & cloud</li>
                  <li>Scaling teams & practices</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-secondary/50 border-border/60">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="size-5 text-primary" />
                  AI & Data
                </CardTitle>
                <CardDescription>Make data work, responsibly.</CardDescription>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground space-y-2">
                <ul className="list-disc pl-5 space-y-1">
                  <li>AI use-cases & pilots</li>
                  <li>Analytics foundations</li>
                  <li>Change & enablement</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Approach */}
        <section className="px-6 md:px-10 py-12 md:py-16 bg-secondary/40">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="rounded-xl overflow-hidden border border-border">
              <img
                src="https://images.pexels.com/photos/3304170/pexels-photo-3304170.jpeg"
                alt="Minimal tech aesthetic"
                className="w-full h-[300px] md:h-[420px] object-cover"
              />
            </div>
            <div className="space-y-5">
              <h2 className="text-2xl md:text-3xl font-bold">
                Outcome-driven, not buzzword-driven
              </h2>
              <p className="text-muted-foreground">
                Clear goals, small bets, tight feedback loops. I pair executive alignment with
                hands-on delivery to unlock momentum and measurable ROI.
              </p>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="p-4 rounded-lg border border-border bg-background/50">
                  <p className="font-semibold">01. Align</p>
                  <p className="text-muted-foreground">Strategy, KPIs, portfolio</p>
                </div>
                <div className="p-4 rounded-lg border border-border bg-background/50">
                  <p className="font-semibold">02. Prove</p>
                  <p className="text-muted-foreground">Pilot, measure, learn</p>
                </div>
                <div className="p-4 rounded-lg border border-border bg-background/50">
                  <p className="font-semibold">03. Scale</p>
                  <p className="text-muted-foreground">Platforms, processes</p>
                </div>
                <div className="p-4 rounded-lg border border-border bg-background/50">
                  <p className="font-semibold">04. Enable</p>
                  <p className="text-muted-foreground">People, change, ops</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Highlights */}
        <section className="px-6 md:px-10 py-12 md:py-16">
          <div className="mb-8 flex items-end justify-between gap-4">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold">Selected results</h2>
              <p className="text-muted-foreground mt-2 max-w-prose">
                Snapshots of outcomes from recent transformations.
              </p>
            </div>
            <Button variant="outline" className="hidden md:inline-flex">
              Request case studies
            </Button>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="border-border">
              <CardHeader>
                <CardTitle>Retail e‑commerce</CardTitle>
                <CardDescription>12 months</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <p className="text-muted-foreground">
                  +38% revenue via product-led growth, improved conversion, and data automation.
                </p>
              </CardContent>
            </Card>
            <Card className="border-border">
              <CardHeader>
                <CardTitle>Financial services</CardTitle>
                <CardDescription>6 months</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <p className="text-muted-foreground">
                  Cut onboarding from 14 days to 48 hours with workflow redesign and AI assist.
                </p>
              </CardContent>
            </Card>
            <Card className="border-border">
              <CardHeader>
                <CardTitle>Manufacturing</CardTitle>
                <CardDescription>9 months</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <p className="text-muted-foreground">
                  Predictive maintenance pilots reduced downtime by 22% across 3 plants.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* CTA */}
        <section className="px-6 md:px-10 py-14 md:py-20 bg-gradient-to-b from-secondary/40 to-transparent">
          <div className="rounded-2xl border border-border p-8 md:p-12 bg-background/60 backdrop-blur">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="space-y-4">
                <h3 className="text-2xl md:text-3xl font-bold">
                  Let’s make the next quarter count
                </h3>
                <p className="text-muted-foreground">
                  Tell me your target outcomes. I’ll propose a lean plan in 48 hours.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Button className="gap-2">
                    Start a project
                    <ArrowRight className="size-4" />
                  </Button>
                  <Button variant="outline">Get a 30‑min intro call</Button>
                </div>
              </div>
              <div className="rounded-xl overflow-hidden border border-border">
                <img
                  src="https://images.pexels.com/photos/5882642/pexels-photo-5882642.jpeg"
                  alt="Professional working on laptop"
                  className="w-full h-[260px] md:h-[300px] object-cover"
                />
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="px-6 md:px-10 py-10 border-t border-border/60 text-sm text-muted-foreground">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p>© {new Date().getFullYear()} James Theeranon. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <a className="hover:text-primary" href="#">
              LinkedIn
            </a>
            <a className="hover:text-primary" href="#">
              Email
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
